import React, { useEffect, useState } from "react";
import "@/styles/emblaShopSlider.css";
import { Box } from "@dxp-feed/ui/Box";
import EmblaSlider, { OffersCarousalSkeleton } from "@/components/EmblaSlider";
import AnimatedQuantityButton from "@/components/QuantityButton";
import FavoriteButton from "@/components/FavoriteButton";
import { getUTMParameter } from "@/helpers/getUTMParameters";
import { sendSalesforceInteraction } from "@/helpers/cdnSdk";
import {
  addDataLayerProductToFavourite,
  addDataLayerProductToList,
  dataLayerProductViewList,
  dataLayerRemoveFormList,
} from "@/helpers/dataLayer";
import { useAppDispatch, useAppSelector } from "@/utils/hooks/reactUseCustomHooks";
import ImageWrapper from "@/components/ImageWrapper";
import { ProductType } from "@/utils/types/product";
import { searchBySingleIndex } from "@/services/algolia";
import OfferCard from "@/components/OfferCard";
import { useStoreDetails } from "@/utils/hooks/useStoreDetails";
import { ALGOLIA_INDEXES } from "@/services/algolia/config";
import { NO_ALCOHOL_STORES } from "@/utils/constant";
import { populateFeaturedProductLists } from "@/utils/redux/featuredProducts";
import { getAlcoholStoreFilter } from "@/helpers/productsFilterHelper";
interface SlideScrollProps {
  base: number;
  xsm: number;
  sm: number;
  ssm: number;
  md: number;
  lg: number;
  xl: number;
  xml: number;
  xxl: number;
}
export default function EmblaShopSlider({
  productIds,
  displayedFrom,
  base,
  xsm,
  sm,
  ssm,
  md,
  lg,
  xl,
  xml,
  xxl,
  homeProductData,
  massOffersData,
  sectionTitle
}: {
  productIds?: string[];
  displayedFrom?: string
  base?: number;
  xsm?: number;
  sm?: number;
  ssm?: number,
  md?: number;
  lg?: number;
  xl?: number;
  xml?: number;
  xxl?: number;
  homeProductData?:any;
  massOffersData?:any;
  sectionTitle?: string;
}) {
  const [productsData, setProductsData] = useState<ProductType[]>([]);
  const { storeID, storeRegion } = useStoreDetails();
  const dispatch = useAppDispatch();

  const fetchData = async () => {
    if (!Array.isArray(productIds) || productIds.length === 0) {
      return;
    }

    const itemsQuery = productIds
      .map((id: string) => `articleNumber:${id}`)
      .join(" OR ");

    const filters = `(${itemsQuery}) AND ${getAlcoholStoreFilter(storeID, storeRegion)}`;

    try {
      const productData = await searchBySingleIndex({
        indexName: ALGOLIA_INDEXES.PRODUCTS.PRIMARY_INDEX,
        searchParams: {
          filters: filters,
        },
      });

      const products = productData?.hits || [];
      dispatch(populateFeaturedProductLists(products))

      setProductsData(products);
    } catch (err: any) {
      console.log("error:", err);
    }
  };

  useEffect(() => {
    if (displayedFrom == "productDetail" && massOffersData?.length > 0) {
      setProductsData(massOffersData)
    } else if (displayedFrom == "homeContentPage") {
      setProductsData(homeProductData)
    } else {
      fetchData();
    }

  }, [productIds, storeID,homeProductData]);

  const customSlidesToScroll: SlideScrollProps = {
    base: base ?? 1,
    xsm: base ?? 2,
    sm: sm ?? 2,
    ssm: ssm ?? 2.8,
    md: md ?? 4,
    lg: lg ?? 4,
    xl: xl ?? 5,
    xml: xml ?? 5,
    xxl: xxl ?? 5,
  };

  const [visibleItems, setVisibleItems] = useState<Set<string>>(new Set());
  const [pendingItems, setPendingItems] = useState<Set<string>>(new Set());
  const [isAllProductCapture, setIsAllProductCapture] = useState(false);
  const [lastSentItems, setLastSentItems] = useState<Set<string>>(new Set());

  const handleVisibilityChange = (item: any, isVisible: boolean) => {
    const itemString = JSON.stringify(item);

    if (isAllProductCapture) return;

    if (isVisible) {
      setVisibleItems((prev) => {
        const newVisible = new Set(prev);
        newVisible.add(itemString);
        return newVisible;
      });
      setPendingItems((prev) => {
        const newPending = new Set(prev);
        newPending.delete(itemString);
        return newPending;
      });
    } else {
      setPendingItems((prev) => {
        const newPending = new Set(prev);
        newPending.add(itemString);
        return newPending;
      });
      setVisibleItems((prev) => {
        const newVisible = new Set(prev);
        newVisible.delete(itemString);
        return newVisible;
      });
    }
  };

  useEffect(() => {
    if (isAllProductCapture || visibleItems.size === 0) return;
    const newItems = new Set(
      [...visibleItems].filter((x) => !lastSentItems.has(x))
    );
    if (newItems.size > 0) {
      const products = Array.from(newItems).map((item) => JSON.parse(item));
      dataLayerProductViewList({
        event: "view_item_list",
        ecommerce: {
          item_list_id: "1",
          item_list_name: "Homepage - Products",
          items: products,
        },
      });
      setLastSentItems((prev) => new Set([...prev, ...newItems]));
    }
    if (visibleItems.size === productsData?.length) {
      setIsAllProductCapture(true);
    }
  }, [
    visibleItems,
    isAllProductCapture,
    productsData?.length,
    lastSentItems,
  ]);
 
  const { isLoading } = useAppSelector((state) => state.gigyaslice);
  return  ( 
    <>
    {!isLoading &&
      <Box as="div">
        {productsData?.length ? <EmblaSlider
          align="center"
          options={{ loop: true, align: "start", dragFree: true }}
          slidesPerView={customSlidesToScroll}
          slidesToScroll={customSlidesToScroll}
          showControls={true}
          thumbnails={false}
          className={`productlistSlider ${productsData?.length > 5 ? "AllProducts" : "EssentialsProducts"}`}
        >
          {productsData?.map((products: any, index: number) => (
            <OfferCard key={products.objectID} item={products} onVisibilityChange={handleVisibilityChange}
              viewItemList={"Homepage - Products"}
              viewItemListId={"1"}
              datalayerTitle={sectionTitle}
              itemPosition={index + 1}
            />
          ))}
        </EmblaSlider> : <OffersCarousalSkeleton />}
      </Box>
    }
    </>
      
  );
}
